<!-- Floating chat dock. Rendered once from app.layout, so it persists across navigation. -->
<ng-container *ngIf="show">

  <!-- Renders the thread's own feedback (send failures, template errors). Bound to the
       MessageService this component provides; the dock is hidden on the Live Conversations page,
       so this never doubles up with that page's toast. -->
  <p-toast position="top-right"></p-toast>

  <!-- ── Pop-out chat windows, stacking leftward from the panel ───────────── -->
  <div class="cdk-windows" [class.cdk-windows--panel-open]="dock.listOpen">
    <div class="cdk-win" *ngFor="let w of dock.windows; trackBy: trackWin"
         [class.cdk-win--min]="w.minimised">
      <div class="cdk-win__head" (click)="dock.toggleMinimise(w.waId)">
        <div class="cdk-win__avatar">{{ convInitials(w.conv) }}</div>
        <div class="cdk-win__who">
          <div class="cdk-win__name">{{ convTitle(w.conv) }}</div>
          <div class="cdk-win__sub" *ngIf="convSubtitle(w.conv) as s">{{ s }}</div>
        </div>
        <!-- Hand over / hand back. Icon-only on purpose: the labelled button this replaces wrapped
             to three lines inside a 330px window. State is carried by colour + tooltip instead, and
             the strip below spells it out whenever the AI is actually paused. -->
        <button type="button" class="cdk-win__btn"
                [class.cdk-win__btn--on]="w.conv.handoff === 'ACTIVE'"
                [disabled]="handoffBusy[w.waId]"
                (click)="toggleHandoff(w); $event.stopPropagation()"
                [pTooltip]="w.conv.handoff === 'ACTIVE' ? 'Hand back to AI' : 'Hand over to agent'"
                tooltipPosition="bottom">
          <i class="pi" [ngClass]="handoffBusy[w.waId] ? 'pi-spinner pi-spin'
                                 : (w.conv.handoff === 'ACTIVE' ? 'pi-sparkles' : 'pi-user')"></i>
        </button>
        <button type="button" class="cdk-win__btn" (click)="dock.toggleMinimise(w.waId); $event.stopPropagation()"
                [pTooltip]="w.minimised ? 'Expand' : 'Minimise'" tooltipPosition="bottom">
          <i class="pi" [ngClass]="w.minimised ? 'pi-chevron-up' : 'pi-minus'"></i>
        </button>
        <button type="button" class="cdk-win__btn" (click)="dock.closeChat(w.waId); $event.stopPropagation()"
                pTooltip="Close" tooltipPosition="bottom">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Only drawn while the AI is paused, so the normal case pays nothing for it. This is what
           the always-on green ACTIVE badge never was: a line that appears when something is true. -->
      <div class="cdk-win__flag" *ngIf="!w.minimised && w.conv.handoff === 'ACTIVE'">
        <i class="pi pi-user"></i> With team — AI has stopped replying
      </div>
      <!-- The SAME thread the inbox renders. A minimised window keeps its DOM but drops the
           thread, which stops that window's 5s poll until it is expanded again. -->
      <div class="cdk-win__body" *ngIf="!w.minimised">
        <app-chat-thread [conversation]="w.conv" [compact]="true"
                         (handoffChanged)="w.conv.handoff = $event"></app-chat-thread>
      </div>
    </div>
  </div>

  <!-- ── Conversation list panel ──────────────────────────────────────────── -->
  <div class="cdk-panel" *ngIf="dock.listOpen">
    <div class="cdk-panel__head">
      <span><i class="pi pi-comments"></i> Chats</span>
      <span class="cdk-panel__spacer"></span>
      <!-- New chat offers ONLY customers whose active case is assigned to this agent. -->
      <button type="button" class="cdk-win__btn" (click)="toggleNewChat()"
              [pTooltip]="newChatOpen ? 'Back to chats' : 'New chat (my cases)'">
        <i class="pi" [ngClass]="newChatOpen ? 'pi-arrow-left' : 'pi-plus'"></i>
      </button>
      <button type="button" class="cdk-win__btn" (click)="dock.toggleList()"><i class="pi pi-times"></i></button>
    </div>

    <!-- ── New chat: search my assigned cases ── -->
    <ng-container *ngIf="newChatOpen">
      <div class="cdk-panel__search">
        <p-iconfield iconPosition="left" styleClass="w-full">
          <p-inputicon class="pi pi-search"></p-inputicon>
          <input pInputText type="text" class="w-full" placeholder="Search my cases by name or number…"
                 [(ngModel)]="newChatQuery" (ngModelChange)="onNewChatQueryChange()" />
        </p-iconfield>
      </div>
      <div class="cdk-panel__list">
        <div class="cdk-empty" *ngIf="newChatSearching"><i class="pi pi-spin pi-spinner"></i></div>
        <div class="cdk-empty" *ngIf="!newChatSearching && newChatQuery.trim().length < 2">
          <span>Type at least 2 characters. Only customers assigned to you are shown.</span>
        </div>
        <div class="cdk-empty" *ngIf="!newChatSearching && newChatQuery.trim().length >= 2 && !newChatResults.length">
          <span>No case of yours matches “{{ newChatQuery }}”.</span>
        </div>
        <div class="cdk-row" *ngFor="let r of newChatResults" (click)="startChatWith(r)"
             [class.cdk-row--disabled]="startingChat || dock.atWindowLimit">
          <div class="cdk-row__avatar">{{ (r.name || r.phone || '?').slice(0, 2).toUpperCase() }}</div>
          <div class="cdk-row__body">
            <div class="cdk-row__top"><span class="cdk-row__name">{{ r.name || r.phone }}</span></div>
            <div class="cdk-row__sub">{{ r.phone }}</div>
          </div>
        </div>
      </div>
    </ng-container>

    <ng-container *ngIf="!newChatOpen">

    <div class="cdk-panel__search">
      <p-iconfield iconPosition="left" styleClass="w-full">
        <p-inputicon class="pi pi-search"></p-inputicon>
        <input pInputText type="text" class="w-full" placeholder="Search name or number…"
               [(ngModel)]="search" (ngModelChange)="onSearchChange()" />
      </p-iconfield>
    </div>

    <div class="cdk-panel__hint" *ngIf="dock.atWindowLimit">
      {{ 3 }} chats open — close one to open another.
    </div>

    <div class="cdk-panel__list">
      <div class="cdk-empty" *ngIf="loading"><i class="pi pi-spin pi-spinner"></i></div>
      <div class="cdk-empty" *ngIf="!loading && !conversations.length">
        <i class="pi pi-inbox"></i><span>No conversations</span>
      </div>

      <div class="cdk-row" *ngFor="let c of conversations; trackBy: trackConv"
           [class.cdk-row--disabled]="dock.atWindowLimit"
           (click)="openChat(c)">
        <div class="cdk-row__avatar">{{ convInitials(c) }}</div>
        <div class="cdk-row__body">
          <div class="cdk-row__top">
            <span class="cdk-row__name">{{ convTitle(c) }}</span>
            <span class="cdk-row__time" *ngIf="shortTime(c.lastMessageAt) as t">{{ t }}</span>
            <span class="cdk-row__badge" *ngIf="c.unreadCount">{{ c.unreadCount > 99 ? '99+' : c.unreadCount }}</span>
          </div>
          <!-- The last message, falling back to the number. The row used to show the number on its
               own, which is already in the title for most of these, so the second line read blank
               or duplicated - a 360px panel drawing one useful word per row. -->
          <div class="cdk-row__sub">{{ c.lastMessageText || convSubtitle(c) || '—' }}</div>
        </div>
      </div>
    </div>
    </ng-container>
  </div>

  <!-- ── Launcher, bottom-right corner. ── -->
  <button class="cdk-fab" (click)="toggleList()" [class.cdk-fab--open]="dock.listOpen"
          aria-label="Chats">
    <i class="pi" [ngClass]="dock.listOpen ? 'pi-times' : 'pi-comments'"></i>
    <span class="cdk-fab__badge" *ngIf="dock.unread && !dock.listOpen">
      {{ dock.unread > 99 ? '99+' : dock.unread }}
    </span>
  </button>
</ng-container>
